import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface TablePagerProps {
  /** Current page (1-based). */
  page: number;
  /** Rows per page. Used to compute the "Showing X–Y of N" range. */
  pageSize: number;
  /** Total row count across all pages. */
  total: number;
  /** Total page count (server-provided). */
  totalPages: number;
  onPageChange: (page: number) => void;
  /**
   * Optional page-size selector. When provided, a Select is rendered with the
   * given options (or a sensible default set).
   */
  onPageSizeChange?: (pageSize: number) => void;
  /** Page-size options shown when `onPageSizeChange` is set. */
  pageSizeOptions?: number[];
  /** Disable both nav buttons (e.g. while a fetch is in flight). */
  disabled?: boolean;
  className?: string;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * Compute the inclusive 1-based "Showing X–Y of N" range for the current
 * page. Returns `{ from: 0, to: 0 }` when there are no rows.
 */
export function pageRange(
  page: number,
  pageSize: number,
  total: number
): { from: number; to: number } {
  if (total <= 0 || pageSize <= 0) return { from: 0, to: 0 };
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return { from, to };
}

/**
 * Controlled Previous/Next pager with a "Showing X–Y of N" readout and an
 * optional page-size selector. Visuals match the Orders screen pager.
 */
export function TablePager({
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  disabled = false,
  className,
}: TablePagerProps) {
  const { from, to } = pageRange(page, pageSize, total);
  const canPrev = page > 1 && !disabled;
  const canNext = page < totalPages && !disabled;

  return (
    <div
      className={cn(
        "mt-4 flex flex-wrap items-center justify-between gap-2 text-sm sm:justify-end",
        className
      )}
    >
      <span className="basis-full text-muted-foreground sm:basis-auto">
        {total > 0 ? `Showing ${from}–${to} of ${total}` : "No results"}
      </span>

      {onPageSizeChange && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <span>Rows</span>
          <Select
            value={String(pageSize)}
            disabled={disabled}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger aria-label="Rows per page" className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="touch"
          disabled={!canPrev}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <Button
          type="button"
          variant="outline"
          size="touch"
          disabled={!canNext}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
